import { yaml as yamlLang } from '@codemirror/lang-yaml';
import { lintGutter } from '@codemirror/lint';
import {
  ScaffolderFieldError,
  ScaffolderFormHeader,
  ScaffolderFormPaper,
  ScaffolderInputHeader,
} from '@internal/plugin-scaffolder-common-field-react';
import { githubLight } from '@uiw/codemirror-theme-github';
import CodeMirror from '@uiw/react-codemirror';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { Box, Checkbox, FormControlLabel, TextField } from '@material-ui/core';
import ErrorOutline from '@material-ui/icons/ErrorOutline';
import ReportProblemOutlined from '@material-ui/icons/ReportProblemOutlined';

import { RepoUrlPicker } from '../components/RepoUrlPicker';
import { useYamlLinter } from '../hooks/useYamlLinter';
import {
  GIT_WRITE_BACK_INFO,
  IMAGES_INFO,
  IMAGE_UPDATER_INFO,
} from '../infoDoc';
import { imagesSchema } from '../schema/imagesSchema';
import { useGeneratorsStyles, useStyles } from '../styles';
import {
  DEFAULT_GIT_WRITE_BACK_CONFIG,
  DEFAULT_IMAGE_UPDATER_CONFIG,
  GitWriteBackConfig,
  ImageUpdaterConfig,
  SourceConfiguration,
} from '../types';
import { gitWriteBackFieldErrors, imagesInputErrors } from '../validation';

interface ImageUpdaterSectionProps {
  value: ImageUpdaterConfig;
  onChange: (value: ImageUpdaterConfig) => void;
  source: SourceConfiguration | null;
  errors: string[];
}

const deriveGitWriteBackFromSource = (
  source: SourceConfiguration | null,
): GitWriteBackConfig => {
  if (!source) return { ...DEFAULT_GIT_WRITE_BACK_CONFIG };

  try {
    const u = new URL(source.repoURL);
    if (u.host === 'github.com') {
      return {
        repository: source.repoURL,
        branch: source.targetRevision,
        target: '',
      };
    }
  } catch {
    // not a URL — fall through
  }

  if (source.helm?.valuesSource) {
    return {
      repository: source.helm.valuesSource.repoURL,
      branch: source.helm.valuesSource.targetRevision,
      target: '',
    };
  }

  return { ...DEFAULT_GIT_WRITE_BACK_CONFIG };
};

const ImageUpdaterSectionInner = ({
  value,
  onChange,
  source,
  errors,
}: ImageUpdaterSectionProps) => {
  const classes = useStyles();
  const generatorClasses = useGeneratorsStyles();
  const sectionRef = useRef<HTMLDivElement>(null);
  const data = value;
  const disabled = !data.enabled;

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const dataRef = useRef(data);
  dataRef.current = data;
  const sourceRef = useRef(source);
  sourceRef.current = source;

  const {
    yamlLinter,
    errors: yamlErrors,
    warnings: yamlWarnings,
  } = useYamlLinter(imagesSchema);

  const handleEnabledChange = useCallback((checked: boolean) => {
    if (!checked) {
      onChangeRef.current({ ...DEFAULT_IMAGE_UPDATER_CONFIG, enabled: false });
      return;
    }
    const current = dataRef.current;
    const gitWriteBackConfig =
      current.gitWriteBackConfig ??
      deriveGitWriteBackFromSource(sourceRef.current);
    onChangeRef.current({
      ...current,
      enabled: true,
      gitWriteBackConfig,
    });
  }, []);

  const handleImagesYAMLChange = useCallback((v: string) => {
    onChangeRef.current({ ...dataRef.current, imagesYAML: v });
  }, []);

  const handleGitWriteBackChange = useCallback(
    (patch: Partial<GitWriteBackConfig>) => {
      const current = dataRef.current;
      const base = current.gitWriteBackConfig ?? DEFAULT_GIT_WRITE_BACK_CONFIG;
      onChangeRef.current({
        ...current,
        gitWriteBackConfig: { ...base, ...patch },
      });
    },
    [],
  );

  const imagesError =
    errors.length > 0 && data.enabled ? imagesInputErrors(data.imagesYAML) : '';
  const writeBackErrors = useMemo(
    () => gitWriteBackFieldErrors(data.gitWriteBackConfig),
    [data.gitWriteBackConfig],
  );

  useEffect(() => {
    if (imagesError) {
      sectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [imagesError]);

  return (
    <div ref={sectionRef}>
      <ScaffolderFormPaper>
        <ScaffolderFormHeader
          title="Image Updater Configuration"
          description="Configure the ArgoCD Image Updater for automatic container image updates."
          divider
          infoDoc={IMAGE_UPDATER_INFO}
        />

        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Enable Image Updater"
            description="Enable or disable the ArgoCD Image Updater for this application."
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data.enabled}
                onChange={e => handleEnabledChange(e.target.checked)}
                color="secondary"
              />
            }
            label="Enable Image Updater"
          />
        </Box>

        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Git Write-back Configuration"
            description="Where ArgoCD Image Updater should push updates. Auto-filled from the Source section."
            infoDoc={GIT_WRITE_BACK_INFO}
          />
          <RepoUrlPicker
            value={data.gitWriteBackConfig?.repository ?? ''}
            onChange={url => handleGitWriteBackChange({ repository: url })}
            label="Repository"
            disabled={disabled}
            freeSolo={false}
            error={Boolean(writeBackErrors.repository)}
            helperText={writeBackErrors.repository}
          />
        </Box>

        <Box className={classes.inputContainer}>
          <TextField
            label="Revision"
            fullWidth
            variant="outlined"
            color="secondary"
            size="small"
            margin="none"
            value={data.gitWriteBackConfig?.branch ?? ''}
            onChange={e => handleGitWriteBackChange({ branch: e.target.value })}
            disabled={disabled}
            error={Boolean(writeBackErrors.branch)}
            helperText={writeBackErrors.branch}
          />
        </Box>

        <Box className={classes.inputContainer}>
          <TextField
            label="Write-back Target"
            fullWidth
            variant="outlined"
            color="secondary"
            size="small"
            margin="none"
            value={data.gitWriteBackConfig?.target ?? ''}
            onChange={e => handleGitWriteBackChange({ target: e.target.value })}
            disabled={disabled}
            helperText="Optional. Use `helmvalues:<path-to-values.yaml>` for Helm value files or `kustomization:<path-to-overlay-directory>` for Kustomize overlays."
          />
        </Box>

        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Images"
            description="Define the container images to be tracked, as YAML. Schema is validated as you type."
            infoDoc={IMAGES_INFO}
          />
        </Box>

        <Box margin={1}>
          <Box className={generatorClasses.codeMirror}>
            <CodeMirror
              value={data.imagesYAML ?? ''}
              minHeight="240px"
              editable={!disabled}
              extensions={[
                typeof yamlLang === 'function' ? yamlLang() : ([] as any),
                lintGutter(),
                yamlLinter,
              ]}
              onChange={handleImagesYAMLChange}
              theme={githubLight}
              placeholder={
                'images:\n  - name: "nginx"\n    commonUpdateSettings:\n      updateStrategy: "semver"\n      allowTags: "regexp:^1\\.\\d+\\.\\d+$"\n    manifestTargets:\n      helm:\n        name: "image.repository"\n        tag: "image.tag"'
              }
            />
          </Box>
          <Box className={generatorClasses.validationContainer}>
            <Box
              display="inline-flex"
              alignItems="center"
              aria-label="error-count"
              title={`${yamlErrors?.length ?? 0} error${
                (yamlErrors?.length ?? 0) === 1 ? '' : 's'
              }`}
              className={
                (yamlErrors?.length ?? 0) > 0
                  ? generatorClasses.validationErrorStyle
                  : generatorClasses.validationDefaultStyle
              }
            >
              <ErrorOutline fontSize="small" style={{ marginRight: 6 }} />
              <span>{yamlErrors?.length ?? 0}</span>
            </Box>
            <Box
              display="inline-flex"
              alignItems="center"
              aria-label="warning-count"
              title={`${yamlWarnings?.length ?? 0} warning${
                (yamlWarnings?.length ?? 0) === 1 ? '' : 's'
              }`}
              className={
                (yamlWarnings?.length ?? 0) > 0
                  ? generatorClasses.validationWarningStyle
                  : generatorClasses.validationDefaultStyle
              }
            >
              <ReportProblemOutlined
                fontSize="small"
                style={{ marginRight: 6 }}
              />
              <span>{yamlWarnings?.length ?? 0}</span>
            </Box>
          </Box>
          {imagesError && (
            <Box className={classes.inputContainer}>
              <ScaffolderFieldError message={imagesError} />
            </Box>
          )}
        </Box>
      </ScaffolderFormPaper>
    </div>
  );
};

export const ImageUpdaterSection = React.memo(ImageUpdaterSectionInner);
